import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../../../../domains/user/hooks/useCurrentUser";
import { saveMainSpecialty } from "../../../../domains/user/saveMainSpecialty";
import { useApiQuery } from "../../../../services/api";
import { HIDDEN_SPECIALTY_IDS } from "../../../../tools/utils";
import { displayToast } from "../../../app/AppToast";
import Loader from "../../../Loader";
import { useSpecialtySearch } from "../../../../domains/app/useLabelSearchAssist";
import { SpecialtyStep } from "../../../../pages/onboarding/steps/SpecialtyStep";
import { StepSearchInput } from "../../../../pages/onboarding/components/StepSearchInput";
import { SettingsSubHeader } from "../SettingsSubHeader";
import KeyboardAvoidingView from "../../../layout/KeyboardAvoidingView";
import { useKeyboardManaged } from "../../../../hooks/use-keyboard-managed";

export function MainSpecialtyView(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const specialtiesQuery = useApiQuery("/webapp/public/tags/parents", {
    durable: true,
  });
  // Controlled search field driven through RHF (no useState in a business
  // component); StepSearchInput takes value/onChange.
  const { watch, setValue } = useForm<{ query: string }>({
    defaultValues: { query: "" },
  });
  const query = watch("query");
  // The search field sits at the bottom of the sheet: without this, iOS pans the
  // whole web view up to reveal it and the filtered rows leave the screen until
  // the keyboard is dismissed.
  useKeyboardManaged();

  const mainSpecialty =
    currentUser === null ? null : currentUser.mainSpecialtyModel;

  // Same pipeline as onboarding OnboardingPage: parent tags minus the globally
  // hidden ones…
  const specialties = useMemo(
    () =>
      specialtiesQuery.state === "success"
        ? specialtiesQuery.body.filter((s) => !HIDDEN_SPECIALTY_IDS.has(s.id))
        : [],
    [specialtiesQuery]
  );

  // …then scoped to the user's profession when it declares a specialty list,
  // falling back to the full list otherwise (mirrors
  // specialtiesForSelectedProfession).
  const scopedSpecialties = useMemo(() => {
    if (currentUser === null) return specialties;
    const profession = currentUser.professionModel;
    if (profession === null || profession.medical_specialties.length === 0) {
      return specialties;
    }
    const allowedTagIds = new Set(profession.medical_specialties);
    return specialties.filter((s) => allowedTagIds.has(s.id));
  }, [currentUser, specialties]);

  const specialtyRows = useSpecialtySearch({
    specialties: scopedSpecialties,
    query,
  });

  const selectedSpecialtyUid = mainSpecialty === null ? "" : mainSpecialty.uid;

  async function handleSelectSpecialty(uid: string): Promise<void> {
    const specialty = specialties.find((s) => s.uid === uid);
    if (specialty === undefined) return;
    if (mainSpecialty !== null && specialty.id === mainSpecialty.id) return;
    const result = await saveMainSpecialty({
      from: mainSpecialty === null ? null : mainSpecialty.id,
      to: specialty.id,
    });
    if (result === "failed") {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    displayToast(
      t("Specialty updated. We'll tune your recommendations."),
      "success"
    );
  }

  const isLoading =
    currentUser === null || specialtiesQuery.state !== "success";

  return (
    <KeyboardAvoidingView
      behavior="padding"
      // The keyboard also covers what BottomPanel keeps under this view (its
      // 20px band and the safe-area inset). 8px of that band is kept as air.
      offset="calc(-12px - env(safe-area-inset-bottom))"
      className="flex h-full flex-col"
    >
      <SettingsSubHeader title={t("My main specialty")} />
      {specialtiesQuery.state === "error" ? (
        <p className="px-5 py-4 text-ui-body text-ds-slate-600">
          {specialtiesQuery.message}
        </p>
      ) : isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          <p className="shrink-0 px-5 pb-3 text-ui-note text-ds-slate-500">
            {t("Select your main specialty to customize your feed.")}
          </p>
          <div className="flex-1 overflow-y-auto scroll-fog-y px-5">
            <SpecialtyStep
              specialties={specialtyRows.rows}
              selectedSpecialty={selectedSpecialtyUid}
              isAssisting={specialtyRows.isAssisting}
              onSelectSpecialty={(uid) => {
                void handleSelectSpecialty(uid);
              }}
              onSubmit={() => undefined}
            />
          </div>
          <div className="shrink-0 px-5 pt-2 pb-6">
            <StepSearchInput
              value={query}
              onChange={(value) => {
                setValue("query", value);
              }}
              placeholder={t("Narrow down my specialty")}
              variant="mobile"
            />
          </div>
        </>
      )}
    </KeyboardAvoidingView>
  );
}
